import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { startSessionTimeout, mapRoleNamesToReduxRole } from '../services/api';
import { store } from '../store/store';
import { setUser as setReduxUser } from '../store/slices/authSlice';
import Logo from '../components/Logo';
import StatusBanner from '../components/StatusBanner';
import { webAuthnApi } from '../services/webauthn';
import useAuthStore from '../store/authStore';
import { Fingerprint, ShieldCheck, KeyRound } from 'lucide-react';
import { logAuditEvent, AUDIT_ACTIONS } from '../utils/auditLogger';
import { QRCodeSVG } from 'qrcode.react';

type LoginStep = 'credentials' | 'totp' | 'totp-setup' | 'webauthn' | 'webauthn-register';

export default function Login2Page() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setUser = useAuthStore((state) => state.setUser);

  const companyFromUrl = searchParams.get('company') || '';

  const [step, setStep] = useState<LoginStep>('credentials');
  const [credentials, setCredentials] = useState({
    companyName: companyFromUrl || localStorage.getItem('lastCompanyName') || '',
    email: '',
    password: '',
  });
  const [hasTotp, setHasTotp] = useState(false);
  const [hasWebAuthn, setHasWebAuthn] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [totpUri, setTotpUri] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const res = await fetch('https://auth.ts1cloud.com/auth/verifyAccess', {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.message === 'Valid Session' && data.data) {
            navigate('/', { replace: true });
          }
        }
      } catch {}
      setIsCheckingSession(false);
    };
    checkSession();
  }, [navigate]);

  // Auto-fetch TOTP setup when entering the setup step
  useEffect(() => {
    if (step === 'totp-setup') fetchTotpSetup();
  }, [step]);

  // Auto-submit TOTP when 6 digits entered
  useEffect(() => {
    if (totpCode.length === 6 && (step === 'totp' || step === 'totp-setup')) {
      if (step === 'totp') handleTotpVerify();
      else handleTotpSetupVerify();
    }
  }, [totpCode]);

  const applySession = (d: any) => {
    setUser({
      id: d.userID, companyID: d.companyID, companyName: d.companyName,
      email: d.email, firstName: d.firstName, lastName: d.lastName,
      roles: Array.isArray(d.roles) ? d.roles : [],
    });
    store.dispatch(setReduxUser({
      id: d.userID, companyID: String(d.companyID), companyName: d.companyName,
      email: d.email ?? '', firstName: d.firstName ?? '', lastName: d.lastName ?? '',
      roles: mapRoleNamesToReduxRole(d.roles),
    }));
    localStorage.setItem('lastCompanyName', d.companyName);
    startSessionTimeout();
  };

  const verifyAndNavigate = async () => {
    await new Promise((r) => setTimeout(r, 200));
    const res = await fetch('https://auth.ts1cloud.com/auth/verifyAccess', {
      method: 'GET', credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    });
    if (!res.ok) throw new Error('Session verification failed');
    const data = await res.json();
    if (data.message !== 'Valid Session' || !data.data) throw new Error('Invalid session');
    applySession(data.data);
    navigate('/', { replace: true });
  };

  // ── Step 1: credentials ────────────────────────────────────────────────────

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const res = await fetch('https://auth.ts1cloud.com/auth/login', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'Origin': window.location.origin },
        body: JSON.stringify(credentials),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Invalid credentials'); return; }

      if (data.status === 'mfa_required') {
        setHasTotp(!!data.hasTotp);
        setHasWebAuthn(!!data.hasWebAuthn);
        if (data.hasTotp) setStep('totp');
        else if (data.hasWebAuthn) setStep('webauthn');
        else setStep('totp-setup');
      } else {
        await verifyAndNavigate();
      }
    } catch {
      setError('Unable to connect. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Step 2a: TOTP verify ───────────────────────────────────────────────────

  const handleTotpVerify = async () => {
    if (totpCode.length !== 6) return;
    setIsLoading(true);
    setError('');
    try {
      const res = await fetch('https://auth.ts1cloud.com/auth/totp/verify', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'Origin': window.location.origin },
        body: JSON.stringify({ code: totpCode }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Invalid code'); setTotpCode(''); return; }
      await logAuditEvent(AUDIT_ACTIONS.USER_LOGIN, `MFA login (TOTP) - Company: ${credentials.companyName}, Email: ${credentials.email}`);
      await verifyAndNavigate();
    } catch {
      setError('Unable to connect. Please try again.');
      setTotpCode('');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Step 2b: TOTP setup ────────────────────────────────────────────────────

  const fetchTotpSetup = async () => {
    setIsLoading(true);
    setError('');
    try {
      const res = await fetch('https://auth.ts1cloud.com/auth/totp/setup', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'Origin': window.location.origin },
      });
      const data = await res.json();
      if (res.ok) { setTotpUri(data.uri); setTotpSecret(data.secret); }
      else setError(data.error || 'Failed to generate setup code');
    } catch {
      setError('Unable to connect. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTotpSetupVerify = async () => {
    if (totpCode.length !== 6) return;
    setIsLoading(true);
    setError('');
    try {
      const res = await fetch('https://auth.ts1cloud.com/auth/totp/verify-setup', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'Origin': window.location.origin },
        body: JSON.stringify({ code: totpCode }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Invalid code'); setTotpCode(''); return; }
      await logAuditEvent(AUDIT_ACTIONS.USER_LOGIN, `MFA setup + login (TOTP) - Company: ${credentials.companyName}, Email: ${credentials.email}`);
      await verifyAndNavigate();
    } catch {
      setError('Unable to connect. Please try again.');
      setTotpCode('');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Step 2c: WebAuthn ──────────────────────────────────────────────────────

  const handleWebAuthnVerify = async () => {
    setIsLoading(true);
    setError('');
    try {
      try {
        await Promise.race([
          fetch('https://auth.ts1cloud.com/auth/verifyAccess', { method: 'GET', credentials: 'include' }),
          new Promise((r) => setTimeout(r, 2000)),
        ]);
      } catch {}
      const options = await webAuthnApi.getAuthenticationOptions();
      const credential = (await navigator.credentials.get({ publicKey: { ...options } })) as PublicKeyCredential;
      if (!credential) throw new Error('No credential received');
      const result = await webAuthnApi.verifyAuthentication(webAuthnApi.prepareAuthenticationCredential(credential));
      if (result?.verified) {
        applySession(result.user);
        await logAuditEvent(AUDIT_ACTIONS.USER_LOGIN, `MFA login (WebAuthn) - Company: ${credentials.companyName}`);
        navigate('/', { replace: true });
      } else {
        throw new Error('Verification failed');
      }
    } catch (err: any) {
      setError(err.name === 'NotAllowedError' ? 'Authentication cancelled. Please try again.' : err.message || 'Authentication failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleWebAuthnRegister = async () => {
    setIsLoading(true);
    setError('');
    try {
      const opts = { companyName: credentials.companyName, email: credentials.email };
      const regOptions = await webAuthnApi.getRegistrationOptions(opts);
      const credential = (await navigator.credentials.create({ publicKey: regOptions })) as PublicKeyCredential;
      if (!credential) throw new Error('No credential created');
      await webAuthnApi.verifyRegistration(opts, webAuthnApi.prepareCredentialForVerify(credential));
      setHasWebAuthn(true);
      setStep('webauthn');
    } catch (err: any) {
      setError(err.name === 'NotAllowedError' ? 'Registration cancelled.' : err.message || 'Registration failed.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (isCheckingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app-canvas">
        <div className="text-gray-600">Checking session...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-pattern py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">

        {/* Header */}
        <div className="bg-app-panel/80 backdrop-blur-sm p-8 rounded-lg shadow-lg">
          <div className="flex justify-center">
            <Logo className="h-12 w-auto mb-6 text-indigo-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">Secure Sign In</h2>
          <div className="mt-2 flex items-center justify-center gap-1 text-sm text-indigo-600">
            <ShieldCheck className="w-4 h-4" />
            <span>Two-factor authentication required</span>
          </div>
        </div>

        <StatusBanner />

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        {/* Step 1 — credentials */}
        {step === 'credentials' && (
          <form className="mt-8 space-y-6" onSubmit={handleCredentialsSubmit}>
            <div className="rounded-md shadow-sm -space-y-px">
              <input
                type="text" required name="company" autoComplete="organization"
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Company Name"
                value={credentials.companyName}
                readOnly={!!companyFromUrl}
                onChange={(e) => setCredentials({ ...credentials, companyName: e.target.value })}
              />
              <input
                type="email" autoComplete="email" required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={credentials.email}
                onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
              />
              <input
                type="password" autoComplete="current-password" required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              />
            </div>
            <button type="submit" disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
              {isLoading ? 'Verifying...' : 'Continue'}
            </button>
            <div className="text-center">
              <Link to="/forgot-password" className="text-sm font-medium text-gray-600 hover:text-indigo-700">
                Forgot your password?
              </Link>
            </div>
          </form>
        )}

        {/* Step 2a — TOTP verify */}
        {step === 'totp' && (
          <div className="mt-8 space-y-4">
            <p className="text-center text-sm text-gray-600">
              Step 2 of 2 — Enter the 6-digit code from your authenticator app.
            </p>
            <input
              type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
              autoFocus autoComplete="one-time-code"
              className="block w-full text-center text-2xl tracking-widest px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="000000"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
            <button onClick={handleTotpVerify} disabled={isLoading || totpCode.length !== 6}
              className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {isLoading ? 'Verifying...' : 'Verify Code'}
            </button>
            <div className="flex flex-col items-center gap-2 pt-1">
              {hasWebAuthn && (
                <button type="button" onClick={() => { setStep('webauthn'); setError(''); }}
                  className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-500">
                  <Fingerprint className="w-4 h-4" /> Use security key instead
                </button>
              )}
              <button type="button" onClick={() => { setStep('credentials'); setError(''); setTotpCode(''); }}
                className="text-sm text-gray-500 hover:text-gray-700">
                ← Back to password
              </button>
            </div>
          </div>
        )}

        {/* Step 2b — TOTP first-time setup */}
        {step === 'totp-setup' && (
          <div className="mt-8 space-y-5">
            <p className="text-center text-sm text-gray-700 font-medium">
              Set up your authenticator app
            </p>
            <p className="text-center text-sm text-gray-500">
              Scan the QR code with Google Authenticator, Authy, or any TOTP app, then enter the 6-digit code below.
            </p>
            {totpUri ? (
              <div className="flex justify-center">
                <div className="p-3 bg-white rounded-lg border border-gray-200 inline-block">
                  <QRCodeSVG value={totpUri} size={180} />
                </div>
              </div>
            ) : (
              <div className="flex justify-center py-8 text-gray-400 text-sm">
                {isLoading ? 'Generating QR code...' : 'Failed to load QR code'}
              </div>
            )}
            {totpSecret && (
              <details className="text-center">
                <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                  Can't scan? Enter code manually
                </summary>
                <p className="mt-1 font-mono text-sm tracking-wider text-gray-700 select-all break-all">
                  {totpSecret}
                </p>
              </details>
            )}
            <input
              type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
              autoComplete="one-time-code"
              className="block w-full text-center text-2xl tracking-widest px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="000000"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
            <button onClick={handleTotpSetupVerify} disabled={isLoading || totpCode.length !== 6}
              className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {isLoading ? 'Verifying...' : 'Activate & Sign In'}
            </button>
            {hasWebAuthn && (
              <button type="button" onClick={() => { setStep('webauthn'); setError(''); }}
                className="w-full flex justify-center items-center gap-1 text-sm text-indigo-600 hover:text-indigo-500">
                <Fingerprint className="w-4 h-4" /> Use security key instead
              </button>
            )}
            <button type="button" onClick={() => { setStep('credentials'); setError(''); setTotpCode(''); }}
              className="w-full text-center text-sm text-gray-500 hover:text-gray-700">
              ← Back to password
            </button>
          </div>
        )}

        {/* Step 2c — WebAuthn verify */}
        {step === 'webauthn' && (
          <div className="mt-8 space-y-4">
            <p className="text-center text-sm text-gray-600">
              Step 2 of 2 — Verify with your security key or biometrics.
            </p>
            <button onClick={handleWebAuthnVerify} disabled={isLoading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
              <Fingerprint className="w-5 h-5 mr-2" />
              {isLoading ? 'Authenticating...' : 'Verify Identity'}
            </button>
            {hasTotp && (
              <button type="button" onClick={() => { setStep('totp'); setError(''); setTotpCode(''); }}
                className="w-full flex justify-center items-center gap-1 text-sm text-indigo-600 hover:text-indigo-500">
                <KeyRound className="w-4 h-4" /> Use authenticator app instead
              </button>
            )}
            <button type="button" onClick={() => { setStep('credentials'); setError(''); }}
              className="w-full text-center text-sm text-gray-500 hover:text-gray-700">
              ← Back to password
            </button>
          </div>
        )}

        {/* Step 2d — WebAuthn register (fallback if no TOTP and no WebAuthn) */}
        {step === 'webauthn-register' && (
          <div className="mt-8 space-y-4">
            <div className="rounded-md bg-blue-50 p-4">
              <p className="text-sm text-blue-700">
                Register a security key or biometric device to complete sign-in.
              </p>
            </div>
            <button onClick={handleWebAuthnRegister} disabled={isLoading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
              <Fingerprint className="w-5 h-5 mr-2" />
              {isLoading ? 'Registering...' : 'Register Security Key'}
            </button>
            <button type="button" onClick={() => { setStep('credentials'); setError(''); }}
              className="w-full text-center text-sm text-gray-500 hover:text-gray-700">
              ← Back to password
            </button>
          </div>
        )}

        <footer className="mt-8 text-center text-xs font-normal text-gray-500">
          © {new Date().getFullYear()} Temperature Monitoring System · Powered by TS1
        </footer>
      </div>
    </div>
  );
}
